import React, { useState, useEffect, useCallback } from 'react';
import { Mail, FileText, Loader2, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../auth/AuthProvider';
import { TemplateLibrary } from '../components/templates/TemplateLibrary';
import { TemplateEditor } from '../components/templates/TemplateEditor';
import { TemplateVersionHistory } from '../components/templates/TemplateVersionHistory';
import type { CommTemplate } from '../components/templates/TemplateLibrary';

// ---- Types ----

type EventTypeFilter = 'all' | 'new_member' | 'new_loan' | 'new_account' | 'account_closed';

interface FilterTab {
  value: EventTypeFilter;
  label: string;
}

const FILTER_TABS: FilterTab[] = [
  { value: 'all', label: 'All' },
  { value: 'new_member', label: 'New Member' },
  { value: 'new_loan', label: 'New Loan' },
  { value: 'new_account', label: 'New Account' },
  { value: 'account_closed', label: 'Account Closed' },
];

// ---- Mock seed data (8 templates matching Plan 22-01 seed) ----

const MOCK_TEMPLATES: CommTemplate[] = [
  {
    id: 'tpl-001', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'new_member_welcome_email', version: 1, isActive: true,
    eventType: 'new_member', channel: 'email',
    subjectTemplate: 'Welcome to {{cu.name}}, {{member.firstName}}!',
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Welcome to ' }, { type: 'mergeVariable', attrs: { variable: 'cu.name' } }, { type: 'text', text: ', ' }, { type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: '! We are thrilled to have you as a member.' }] }] }),
    isSystem: true, createdBy: null, approvedBy: 'system',
    createdAt: new Date(Date.now() - 30 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 30 * 24 * 3600000).toISOString(),
  },
  {
    id: 'tpl-002', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'new_member_welcome_sms', version: 1, isActive: true,
    eventType: 'new_member', channel: 'sms',
    subjectTemplate: null,
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Hi ' }, { type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: '! Welcome to ' }, { type: 'mergeVariable', attrs: { variable: 'cu.name' } }, { type: 'text', text: '. Call us at ' }, { type: 'mergeVariable', attrs: { variable: 'cu.phone' } }, { type: 'text', text: '.' }] }] }),
    isSystem: true, createdBy: null, approvedBy: 'system',
    createdAt: new Date(Date.now() - 30 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 30 * 24 * 3600000).toISOString(),
  },
  {
    id: 'tpl-003', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'new_member_onboarding_day3_email', version: 2, isActive: true,
    eventType: 'new_member', channel: 'email',
    subjectTemplate: '3 tips to get the most from your account',
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Hi ' }, { type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: ', here are 3 tips to maximize your membership...' }] }] }),
    isSystem: false, createdBy: 'staff-j-chen', approvedBy: 'staff-m-lee',
    createdAt: new Date(Date.now() - 7 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 7 * 24 * 3600000).toISOString(),
  },
  {
    id: 'tpl-004', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'new_loan_approved_email', version: 1, isActive: true,
    eventType: 'new_loan', channel: 'email',
    subjectTemplate: 'Your {{loan.type}} loan has been approved!',
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Congratulations, ' }, { type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: '! Your ' }, { type: 'mergeVariable', attrs: { variable: 'loan.type' } }, { type: 'text', text: ' loan of ' }, { type: 'mergeVariable', attrs: { variable: 'loan.amount' } }, { type: 'text', text: ' has been approved.' }] }] }),
    isSystem: true, createdBy: null, approvedBy: 'system',
    createdAt: new Date(Date.now() - 20 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 20 * 24 * 3600000).toISOString(),
  },
  {
    id: 'tpl-005', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'new_loan_approved_sms', version: 1, isActive: true,
    eventType: 'new_loan', channel: 'sms',
    subjectTemplate: null,
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: ', your loan is approved! Term: ' }, { type: 'mergeVariable', attrs: { variable: 'loan.term' } }, { type: 'text', text: '. Call ' }, { type: 'mergeVariable', attrs: { variable: 'cu.phone' } }] }] }),
    isSystem: false, createdBy: 'staff-r-park', approvedBy: null,
    createdAt: new Date(Date.now() - 2 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 2 * 24 * 3600000).toISOString(),
  },
  {
    id: 'tpl-006', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'new_account_opened_email', version: 1, isActive: true,
    eventType: 'new_account', channel: 'email',
    subjectTemplate: 'Your new {{account.type}} account is ready',
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Hi ' }, { type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: ', your ' }, { type: 'mergeVariable', attrs: { variable: 'account.type' } }, { type: 'text', text: ' account ending in ' }, { type: 'mergeVariable', attrs: { variable: 'account.number_last4' } }, { type: 'text', text: ' is now open.' }] }] }),
    isSystem: true, createdBy: null, approvedBy: 'system',
    createdAt: new Date(Date.now() - 15 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 15 * 24 * 3600000).toISOString(),
  },
  {
    id: 'tpl-007', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'account_closed_confirmation_email', version: 1, isActive: true,
    eventType: 'account_closed', channel: 'email',
    subjectTemplate: 'Account closure confirmation',
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Hi ' }, { type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: ', your ' }, { type: 'mergeVariable', attrs: { variable: 'account.type' } }, { type: 'text', text: ' account ending in ' }, { type: 'mergeVariable', attrs: { variable: 'account.number_last4' } }, { type: 'text', text: ' has been closed.' }] }] }),
    isSystem: true, createdBy: null, approvedBy: 'system',
    createdAt: new Date(Date.now() - 10 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 10 * 24 * 3600000).toISOString(),
  },
  {
    id: 'tpl-008', tenantId: 'a1b2c3d4-0003-4000-8000-333333333333',
    templateKey: 'account_closed_confirmation_sms', version: 1, isActive: true,
    eventType: 'account_closed', channel: 'sms',
    subjectTemplate: null,
    bodyTemplate: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'mergeVariable', attrs: { variable: 'member.firstName' } }, { type: 'text', text: ', your account ending ' }, { type: 'mergeVariable', attrs: { variable: 'account.number_last4' } }, { type: 'text', text: ' is now closed. Questions? Call ' }, { type: 'mergeVariable', attrs: { variable: 'cu.phone' } }] }] }),
    isSystem: false, createdBy: 'staff-j-chen', approvedBy: null,
    createdAt: new Date(Date.now() - 1 * 24 * 3600000).toISOString(), updatedAt: new Date(Date.now() - 1 * 24 * 3600000).toISOString(),
  },
];

// ---- Helpers ----

function getJwt(): string {
  return localStorage.getItem('crm_dev_token') ?? '';
}

// ---- Toast ----

interface ToastProps {
  message: string;
  onDismiss: () => void;
}

function Toast({ message, onDismiss }: ToastProps): React.ReactElement {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 bg-pass text-white rounded-xl shadow-lg text-sm font-medium animate-fade-in">
      <Sparkles className="h-4 w-4 flex-shrink-0" aria-hidden />
      {message}
      <button type="button" onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100 text-lg leading-none" aria-label="Dismiss">
        &times;
      </button>
    </div>
  );
}

// ---- TemplatesPage ----

export function TemplatesPage(): React.ReactElement {
  const { user } = useAuth();

  const [templates, setTemplates] = useState<CommTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<CommTemplate | null>(null);
  const [versions, setVersions] = useState<CommTemplate[]>([]);
  const [activeFilter, setActiveFilter] = useState<EventTypeFilter>('all');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // ---- Fetch templates ----

  const fetchTemplates = useCallback(async (filter: EventTypeFilter): Promise<void> => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? '/api/templates'
        : `/api/templates?eventType=${filter}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as CommTemplate[];
      setTemplates(data);
    } catch {
      // API unavailable in dev — use mock data
      const filtered = filter === 'all'
        ? MOCK_TEMPLATES
        : MOCK_TEMPLATES.filter((t) => t.eventType === filter);
      setTemplates(filtered);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates(activeFilter);
  }, [activeFilter, fetchTemplates]);

  // ---- Fetch version history ----

  const fetchVersions = useCallback(async (templateKey: string): Promise<void> => {
    try {
      const res = await fetch(`/api/templates/${templateKey}/history`, {
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as CommTemplate[];
      setVersions(data);
    } catch {
      // Mock: single version from current template
      const t = MOCK_TEMPLATES.find((m) => m.templateKey === templateKey);
      setVersions(t !== undefined ? [t] : []);
    }
  }, []);

  // ---- Select template ----

  function handleSelectTemplate(key: string): void {
    const t = templates.find((tmpl) => tmpl.templateKey === key) ?? null;
    setSelectedTemplate(t);
    if (t !== null) {
      void fetchVersions(key);
    }
  }

  // ---- Save version ----

  async function handleSave(json: string, subject?: string): Promise<void> {
    if (selectedTemplate === null) return;
    setActionLoading(true);
    try {
      const res = await fetch(`/api/templates/${selectedTemplate.templateKey}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getJwt()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bodyTemplate: json, subjectTemplate: subject }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = (await res.json()) as CommTemplate;
      setSelectedTemplate(updated);
      setToast('Template version saved.');
      void fetchVersions(selectedTemplate.templateKey);
    } catch {
      setToast('Saved locally (API unavailable in dev).');
    } finally {
      setActionLoading(false);
    }
  }

  // ---- Approve ----

  async function handleApprove(): Promise<void> {
    if (selectedTemplate === null) return;
    setActionLoading(true);
    try {
      const res = await fetch(`/api/templates/${selectedTemplate.id}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = (await res.json()) as CommTemplate;
      setSelectedTemplate(updated);
      setToast('Template approved.');
    } catch {
      // Mock approval
      setSelectedTemplate((prev) =>
        prev !== null ? { ...prev, approvedBy: user?.email ?? 'staff' } : null,
      );
      setToast('Template approved (dev mode).');
    } finally {
      setActionLoading(false);
    }
  }

  // ---- Generate variant ----

  async function handleGenerateVariant(): Promise<void> {
    if (selectedTemplate === null) return;
    setActionLoading(true);
    try {
      const res = await fetch(`/api/templates/${selectedTemplate.id}/generate-variant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setToast('Variant sent to review queue.');
    } catch {
      setToast('Variant sent to review queue (dev mode).');
    } finally {
      setActionLoading(false);
    }
  }

  // ---- Customize system template ----

  async function handleCustomize(): Promise<void> {
    if (selectedTemplate === null) return;
    setActionLoading(true);
    try {
      const res = await fetch(`/api/templates/${selectedTemplate.templateKey}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getJwt()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bodyTemplate: selectedTemplate.bodyTemplate,
          subjectTemplate: selectedTemplate.subjectTemplate,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = (await res.json()) as CommTemplate;
      setSelectedTemplate(created);
      setToast('Custom version created — now editable.');
    } catch {
      // Mock: create local copy
      const copy: CommTemplate = {
        ...selectedTemplate,
        id: `tpl-custom-${Date.now()}`,
        isSystem: false,
        version: selectedTemplate.version + 1,
        createdBy: user?.email ?? 'staff',
        approvedBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setSelectedTemplate(copy);
      setToast('Custom version created (dev mode) — now editable.');
    } finally {
      setActionLoading(false);
    }
  }

  // ---- Derived state ----

  const displayedTemplates = templates;

  // ---- Render ----

  return (
    <div className="flex flex-col h-full gap-0">

      {/* ---- Page header ---- */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-info-soft border border-info-soft">
          <Mail className="h-5 w-5 text-brand-dark" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-ink leading-tight">Templates</h1>
          <p className="text-sm text-ink-muted">Lifecycle communication templates</p>
        </div>
      </div>

      {/* ---- Filter tabs ---- */}
      <div className="flex items-center gap-0 border-b border-line mb-4">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => {
              setActiveFilter(tab.value);
              setSelectedTemplate(null);
              setVersions([]);
            }}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeFilter === tab.value
                ? 'border-brand text-brand-dark'
                : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-line-strong',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ---- Main content: sidebar + editor ---- */}
      <div className="flex flex-1 gap-0 overflow-hidden rounded-xl border border-line bg-white shadow-sm min-h-[600px]">

        {/* ---- Left sidebar: template library ---- */}
        <div className="w-80 flex-shrink-0 border-r border-line overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-ink-muted" aria-label="Loading templates" />
            </div>
          ) : (
            <TemplateLibrary
              templates={displayedTemplates}
              selectedKey={selectedTemplate?.templateKey ?? null}
              onSelect={handleSelectTemplate}
            />
          )}
        </div>

        {/* ---- Right panel: editor + version history ---- */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedTemplate === null ? (
            // Empty state
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center p-8">
              <div className="w-12 h-12 rounded-full bg-sunken flex items-center justify-center">
                <FileText className="h-6 w-6 text-ink-muted" aria-hidden />
              </div>
              <p className="text-sm font-medium text-ink-muted">Select a template from the library</p>
              <p className="text-xs text-ink-muted">Click any template on the left to view and edit its content.</p>
            </div>
          ) : (
            <div className="flex flex-col flex-1 overflow-hidden">

              {/* Editor header with action buttons */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-bg/50 flex-shrink-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink truncate">
                    {selectedTemplate.templateKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </p>
                  <p className="text-xs text-ink-muted mt-0.5">
                    v{selectedTemplate.version}
                    {selectedTemplate.isSystem ? ' · System template' : ''}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  {/* Customize (system only) */}
                  {selectedTemplate.isSystem && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => { void handleCustomize(); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-line bg-white text-ink-soft hover:bg-bg disabled:opacity-50 transition-colors"
                    >
                      Customize
                    </button>
                  )}

                  {/* Approve (non-system, not yet approved) */}
                  {!selectedTemplate.isSystem && selectedTemplate.approvedBy === null && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => { void handleApprove(); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-pass-soft bg-pass-soft text-pass hover:bg-pass-soft disabled:opacity-50 transition-colors"
                    >
                      Approve
                    </button>
                  )}

                  {/* Generate variant */}
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => { void handleGenerateVariant(); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-running text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    <Sparkles className="h-3.5 w-3.5" aria-hidden />
                    Generate Variant
                  </button>
                </div>
              </div>

              {/* Editor + version history split */}
              <div className="flex flex-1 overflow-hidden">
                {/* Tiptap editor */}
                <div className="flex-1 flex flex-col overflow-hidden border-r border-sunken">
                  <TemplateEditor
                    key={selectedTemplate.id}
                    initialJson={selectedTemplate.bodyTemplate}
                    channel={selectedTemplate.channel as 'email' | 'sms'}
                    eventType={selectedTemplate.eventType}
                    onSave={(json, subject) => { void handleSave(json, subject); }}
                    isSystem={selectedTemplate.isSystem}
                    templateKey={selectedTemplate.templateKey}
                  />
                </div>

                {/* Version history panel */}
                <div className="w-64 flex-shrink-0 overflow-y-auto">
                  <TemplateVersionHistory versions={versions} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---- Toast notification ---- */}
      {toast !== null && (
        <Toast message={toast} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
